import { AdvancedAnalyticsEntityType } from 'components/modal/modalList/graphDetails/components/AdvancedAnalyticsEntityTypes/AdvancedAnalyticsEntityTypes';
import { activeWlConfigStore, WlConfig } from 'config/wls/wlConfig';
import { Options } from 'highcharts';
import { buildQuickFilters } from 'helpers/quickFiltersBuilder';
import { QuickFilterConfig } from 'types/QuickFilters';

export const getComponentVersion = (): string => {
  const config = activeWlConfigStore.getActiveWlConfig();
  return config?.isNewComponentsEnabled && config.componentsVersion
    ? config.componentsVersion
    : 'default';
};

export const getMenuConfig = (fallbackMenu: any[]) => {
  const config = activeWlConfigStore.getActiveWlConfig();
  return config?.menuConfiguration ?? fallbackMenu;
};

export const getQuickFiltersConfig = (
  tableName: string,
  defaultConfig: QuickFilterConfig[]
) => {
  const config = activeWlConfigStore.getActiveWlConfig();

  const overrides = config?.quickFiltersOverride;

  return overrides
    ? buildQuickFilters(defaultConfig, overrides, tableName)
    : defaultConfig;
};

export const getTableConfig = (tableName: string, fallback?: any) => {
  const config = activeWlConfigStore.getActiveWlConfig();

  return config?.tableConfig?.[tableName] ?? fallback;
};

/**
 * Returns resolved table props ready to spread into a table component.
 *
 * Both `rowRender` and `headerRender` are stored in wlConfig as factories
 * that receive a "render bag" — an object the page builds from its own props
 * and methods. This lets WL renderers access anything they need (callbacks,
 * translate, config, etc.) without the resolver needing to know about it.
 *
 *   headerRender: (bag) => ReactNode
 *   rowRender:    (bag) => (data, index) => ReactNode
 *
 * Props absent from the WL config are simply omitted, so the table falls back
 * to its own internal rendering logic.
 */
export const getResolvedTableProps = (
  tableName: string,
  bag: Record<string, any> = {}
): Record<string, any> => {
  const config = getTableConfig(tableName);
  if (!config?.props) return {};

  const { headerRender, rowRender, ...rest } = config.props;

  return {
    ...rest,
    ...(headerRender ? { headerRender: headerRender(bag) } : {}),
    ...(rowRender ? { rowRender: rowRender(bag) } : {}),
  };
};

export const resolveWlValue = <T>(
  selector: (config: WlConfig) => T | undefined,
  fallback: T
): T => {
  const config = activeWlConfigStore.getActiveWlConfig();

  if (!config) {
    return fallback;
  }

  return selector(config) ?? fallback;
};

export const getWlChartSettingsFormConfig = (
  chartType: AdvancedAnalyticsEntityType,
  fallback?: any
) => {
  const config = activeWlConfigStore.getActiveWlConfig();

  return config?.chartSettingsFormConfig?.[chartType] ?? fallback;
};

export const getWlChartStyleTransformer = (): Options | null => {
  const config = activeWlConfigStore.getActiveWlConfig();

  return config?.chartStyleTransformer ?? null;
};

export const getWlChartOptionsTransformer = ():
  | ((options: Options, chartType?: string) => Options)
  | null => {
  const config = activeWlConfigStore.getActiveWlConfig();

  return config?.chartOptionsTransformer ?? null;
};

export const getHiddenProfileFields = (): string[] => {
  const config = activeWlConfigStore.getActiveWlConfig();

  return config?.profileFieldsOverride
    ? Object.entries(config.profileFieldsOverride)
        .filter(([, value]) => value.hidden)
        .map(([key]) => key)
    : [];
};
